import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Button,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  CircularProgress,
  Pagination,
  Chip,
  Tooltip,
  Alert,
  AlertTitle,
  List,
  ListItem,
  ListItemText,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Refresh as RefreshIcon,
  UploadFile as UploadFileIcon,
} from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext.tsx';
import { useSnackbar } from '../contexts/SnackbarContext.tsx';
import { employeeApi, Employee, PaginatedEmployeesResponse, EmployeeImportSummary } from '../services/employeeService.ts';
import { formatPhoneNumber } from '../utils/formatPhone.ts';

const EmployeesPage: React.FC = () => {
  const { sessionToken, user } = useAuth();
  const isAdmin = user?.type === 'admin';
  const { showError, showSuccess } = useSnackbar();
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [employees, setEmployees] = useState<Employee[]>([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [lastImportSummary, setLastImportSummary] = useState<EmployeeImportSummary | null>(null);
  const [page, setPage] = useState(1);
  const [limit] = useState(10);
  const [totalPages, setTotalPages] = useState(0);
  const [total, setTotal] = useState(0);
  const [hasNext, setHasNext] = useState(false);
  const [hasPrev, setHasPrev] = useState(false);

  // Dialog states
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingEmployee, setEditingEmployee] = useState<Employee | null>(null);
  const [employeeFirstName, setEmployeeFirstName] = useState('');
  const [employeeLastName, setEmployeeLastName] = useState('');
  const [employeeEmail, setEmployeeEmail] = useState('');
  const [employeePhone, setEmployeePhone] = useState('');
  const [employeeExtId, setEmployeeExtId] = useState('');
  const [employeeDesignation, setEmployeeDesignation] = useState('');
  const [dialogLoading, setDialogLoading] = useState(false);
  const [autoSaveStatus, setAutoSaveStatus] = useState<'idle' | 'pending' | 'saving' | 'saved' | 'error'>('idle');
  const [autoSaveError, setAutoSaveError] = useState('');
  const autoSaveTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    return () => {
      if (autoSaveTimeoutRef.current) clearTimeout(autoSaveTimeoutRef.current);
    };
  }, []);

  const fetchEmployees = async (pageNum: number = 1) => {
    if (!sessionToken) return;

    setLoading(true);

    try {
      const response: PaginatedEmployeesResponse = await employeeApi.getEmployees(
        { page: pageNum, limit },
        sessionToken
      );

      setEmployees(response.data);
      setPage(response.page);
      setTotalPages(response.totalPages);
      setTotal(response.total);
      setHasNext(response.hasNext);
      setHasPrev(response.hasPrev);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to fetch employees';
      showError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchEmployees();
  }, [sessionToken]);

  const handlePageChange = (event: React.ChangeEvent<unknown>, value: number) => {
    setPage(value);
    fetchEmployees(value);
  };

  const handleAddEmployee = () => {
    setEditingEmployee(null);
    setEmployeeFirstName('');
    setEmployeeLastName('');
    setEmployeeEmail('');
    setEmployeePhone('');
    setEmployeeExtId('');
    setEmployeeDesignation('');
    setDialogOpen(true);
  };

  const handleEditEmployee = (employee: Employee) => {
    if (autoSaveTimeoutRef.current) {
      clearTimeout(autoSaveTimeoutRef.current);
      autoSaveTimeoutRef.current = null;
    }
    setAutoSaveStatus('idle');
    setAutoSaveError('');
    setEditingEmployee(employee);
    setEmployeeFirstName(employee.firstName || '');
    setEmployeeLastName(employee.lastName || '');
    setEmployeeEmail(employee.email || '');
    setEmployeePhone(employee.phone || '');
    setEmployeeExtId(employee.employeeId ?? '0');
    setEmployeeDesignation(employee.designation ?? '');
    setDialogOpen(true);
  };

  // Auto-saves ~900ms after the last change to any field, while editing an existing
  // employee. Creating a new employee still requires the explicit Save button below.
  const performAutoSave = useCallback(
    async (id: number, firstName: string, lastName: string, email: string, phone: string, extId: string, designation: string) => {
      if (!sessionToken) return;
      if (!firstName.trim() || !lastName.trim()) {
        setAutoSaveStatus('error');
        setAutoSaveError('First Name and Last Name are required.');
        return;
      }
      setAutoSaveStatus('saving');
      try {
        await employeeApi.updateEmployee(
          id,
          {
            firstName: firstName.trim(),
            lastName: lastName.trim(),
            email: email.trim() || undefined,
            phone: phone.trim() || undefined,
            employeeId: (extId || '0').trim(),
            designation: designation.trim() || undefined,
          },
          sessionToken
        );
        setAutoSaveStatus('saved');
        fetchEmployees(page);
      } catch (err) {
        setAutoSaveStatus('error');
        setAutoSaveError(err instanceof Error ? err.message : 'Failed to save changes');
      }
    },
    [sessionToken, page]
  );

  const scheduleAutoSave = (nextFirstName: string, nextLastName: string, nextEmail: string, nextPhone: string, nextExtId: string, nextDesignation: string) => {
    if (!editingEmployee) return;
    const employeeId = editingEmployee.id;
    if (autoSaveTimeoutRef.current) clearTimeout(autoSaveTimeoutRef.current);
    setAutoSaveStatus('pending');
    autoSaveTimeoutRef.current = setTimeout(() => {
      autoSaveTimeoutRef.current = null;
      performAutoSave(employeeId, nextFirstName, nextLastName, nextEmail, nextPhone, nextExtId, nextDesignation);
    }, 900);
  };

  const handleCloseDialog = async () => {
    if (autoSaveTimeoutRef.current && editingEmployee) {
      clearTimeout(autoSaveTimeoutRef.current);
      autoSaveTimeoutRef.current = null;
      await performAutoSave(editingEmployee.id, employeeFirstName, employeeLastName, employeeEmail, employeePhone, employeeExtId, employeeDesignation);
    }
    setDialogOpen(false);
  };

  const handleDeleteEmployee = async (id: number) => {
    if (!sessionToken) return;

    if (window.confirm('Are you sure you want to delete this employee?')) {
      try {
        await employeeApi.deleteEmployee(id, sessionToken);
        showSuccess('Employee deleted successfully!');
        fetchEmployees(page);
      } catch (err) {
        const errorMessage = err instanceof Error ? err.message : 'Failed to delete employee';
        showError(errorMessage);
      }
    }
  };

  const handleSaveEmployee = async () => {
    if (!sessionToken || !employeeFirstName.trim() || !employeeLastName.trim()) return;

    setDialogLoading(true);

    try {
      const payload = {
        firstName: employeeFirstName.trim(),
        lastName: employeeLastName.trim(),
        email: employeeEmail.trim() || undefined,
        phone: employeePhone.trim() || undefined,
        employeeId: (employeeExtId || '0').trim(),
        designation: employeeDesignation.trim() || undefined,
      };
      if (editingEmployee) {
        await employeeApi.updateEmployee(editingEmployee.id, payload, sessionToken);
      } else {
        await employeeApi.createEmployee(payload, sessionToken);
      }

      setDialogOpen(false);
      showSuccess(editingEmployee ? 'Employee updated successfully!' : 'Employee created successfully!');
      fetchEmployees(page);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to save employee';
      showError(errorMessage);
    } finally {
      setDialogLoading(false);
    }
  };

  const handleUploadClick = () => {
    fileInputRef.current?.click();
  };

  const handleFileSelected = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file || !sessionToken) return;

    setUploading(true);
    setLastImportSummary(null);
    try {
      const summary = await employeeApi.importCsv(file, sessionToken);
      setLastImportSummary(summary);
      if (summary.imported > 0 || summary.updated > 0) {
        const parts = [];
        if (summary.imported > 0) parts.push(`${summary.imported} added`);
        if (summary.updated > 0) parts.push(`${summary.updated} overwritten`);
        showSuccess(`${parts.join(', ')}.`);
      } else {
        showError('No employees were imported from that file.');
      }
      fetchEmployees(1);
    } catch (err) {
      showError(err instanceof Error ? err.message : 'Failed to import CSV');
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const formatDate = (timestamp: number) => {
    return new Date(timestamp * 1000).toLocaleDateString();
  };

  if (loading && employees.length === 0) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h6" sx={{ color: "#7f0000", fontWeight: 700, flex: "0 0 auto", whiteSpace: "nowrap" }}>
          Date: {new Date().toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric" })}
        </Typography>
        <Typography variant="h4" component="h1" sx={{ flex: 1, textAlign: 'center', fontWeight: 700, textTransform: 'uppercase' }}>
          Employees
        </Typography>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={() => fetchEmployees(page)}
          >
            Refresh
          </Button>
          {isAdmin && (
            <>
              <Button
                variant="outlined"
                startIcon={uploading ? <CircularProgress size={16} /> : <UploadFileIcon />}
                onClick={handleUploadClick}
                disabled={uploading}
              >
                {uploading ? 'Importing...' : 'Upload CSV'}
              </Button>
              <input
                ref={fileInputRef}
                type="file"
                accept=".csv,text/csv"
                hidden
                onChange={handleFileSelected}
              />
            </>
          )}
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={handleAddEmployee}
          >
            Add Employee
          </Button>
        </Box>
      </Box>

      {lastImportSummary && (
        <Alert
          severity={lastImportSummary.imported > 0 || lastImportSummary.updated > 0 ? 'success' : 'warning'}
          sx={{ mb: 3 }}
          onClose={() => setLastImportSummary(null)}
        >
          <AlertTitle>Import Results</AlertTitle>
          {lastImportSummary.totalRows} row{lastImportSummary.totalRows !== 1 ? 's' : ''} read &middot; {lastImportSummary.imported} added &middot; {lastImportSummary.updated} overwritten &middot; {lastImportSummary.skipped} skipped
          {lastImportSummary.errors.length > 0 && (
            <List dense sx={{ mt: 1 }}>
              {lastImportSummary.errors.slice(0, 10).map((e, i) => (
                <ListItem key={i} sx={{ py: 0 }}>
                  <ListItemText primaryTypographyProps={{ variant: 'caption' }} primary={e} />
                </ListItem>
              ))}
              {lastImportSummary.errors.length > 10 && (
                <ListItem sx={{ py: 0 }}>
                  <ListItemText primaryTypographyProps={{ variant: 'caption' }} primary={`...and ${lastImportSummary.errors.length - 10} more`} />
                </ListItem>
              )}
            </List>
          )}
        </Alert>
      )}

      <Box sx={{ mb: 1 }}>
        <Typography variant="body2" color="text.secondary">
          {total} employee{total !== 1 ? 's' : ''} total{totalPages > 1 ? ` · Page ${page} of ${totalPages}` : ''}
        </Typography>
      </Box>

      <Paper sx={{ width: '100%', overflow: 'hidden', border: '2px solid #9e9e9e' }}>
        <TableContainer>
          <Table sx={{ borderCollapse: 'collapse' }}>
            <TableHead>
              <TableRow sx={{ backgroundColor: '#b71c1c' }}>
                {['ID', 'Name', 'Email', 'Phone', 'Employee ID', 'Role', 'Created Date', 'Actions'].map((header) => (
                  <TableCell
                    key={header}
                    align={header === 'Actions' ? 'center' : 'left'}
                    sx={{
                      color: '#fff',
                      fontWeight: 700,
                      fontSize: '0.85rem',
                      letterSpacing: '0.05em',
                      textTransform: 'uppercase',
                      border: '2px solid #7f0000',
                      py: 1.5,
                      px: 2,
                    }}
                  >
                    {header}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {employees.map((employee, index) => (
                <TableRow
                  key={employee.id}
                  sx={{
                    backgroundColor: index % 2 === 0 ? '#fff' : '#f5f8ff',
                    '&:hover': { backgroundColor: '#e3f2fd' },
                  }}
                >
                  <TableCell sx={{ border: '2px solid #9e9e9e', py: 1.25, px: 2, width: 60 }}>
                    <Typography variant="body2" color="text.secondary">
                      {employee.id}
                    </Typography>
                  </TableCell>
                  <TableCell sx={{ border: '2px solid #9e9e9e', py: 1.25, px: 2 }}>
                    <Typography variant="body2" fontWeight="600">
                      {employee.name}
                    </Typography>
                  </TableCell>
                  <TableCell sx={{ border: '2px solid #9e9e9e', py: 1.25, px: 2 }}>
                    <Typography variant="body2">
                      {employee.email || '—'}
                    </Typography>
                  </TableCell>
                  <TableCell sx={{ border: '2px solid #9e9e9e', py: 1.25, px: 2 }}>
                    <Typography variant="body2">
                      {formatPhoneNumber(employee.phone) || '—'}
                    </Typography>
                  </TableCell>
                  <TableCell sx={{ border: '2px solid #9e9e9e', py: 1.25, px: 2 }}>
                    <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                      {employee.employeeId ?? '0'}
                    </Typography>
                  </TableCell>
                  <TableCell sx={{ border: '2px solid #9e9e9e', py: 1.25, px: 2 }}>
                    <Typography variant="body2">
                      {employee.designation || '—'}
                    </Typography>
                  </TableCell>
                  <TableCell sx={{ border: '2px solid #9e9e9e', py: 1.25, px: 2 }}>
                    <Chip
                      label={formatDate(employee.createdDate)}
                      variant="outlined"
                      size="small"
                      sx={{ borderRadius: 1 }}
                    />
                  </TableCell>
                  <TableCell align="center" sx={{ border: '2px solid #9e9e9e', py: 1.25, px: 2, width: 100 }}>
                    <Tooltip title="Edit Employee">
                      <IconButton
                        size="small"
                        onClick={() => handleEditEmployee(employee)}
                        color="primary"
                      >
                        <EditIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Delete Employee">
                      <IconButton
                        size="small"
                        onClick={() => handleDeleteEmployee(employee.id)}
                        color="error"
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>

        {totalPages > 1 && (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
            <Pagination
              count={totalPages}
              page={page}
              onChange={handlePageChange}
              color="primary"
              showFirstButton
              showLastButton
            />
          </Box>
        )}
      </Paper>

      {/* Add/Edit Employee Dialog */}
      <Dialog open={dialogOpen} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        <DialogTitle>
          {editingEmployee ? 'Edit Employee' : 'Add New Employee'}
        </DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="First Name"
            fullWidth
            variant="outlined"
            value={employeeFirstName}
            onChange={(e) => {
              const value = e.target.value;
              setEmployeeFirstName(value);
              scheduleAutoSave(value, employeeLastName, employeeEmail, employeePhone, employeeExtId, employeeDesignation);
            }}
            disabled={dialogLoading}
          />
          <TextField
            margin="dense"
            label="Last Name"
            fullWidth
            variant="outlined"
            value={employeeLastName}
            onChange={(e) => {
              const value = e.target.value;
              setEmployeeLastName(value);
              scheduleAutoSave(employeeFirstName, value, employeeEmail, employeePhone, employeeExtId, employeeDesignation);
            }}
            disabled={dialogLoading}
          />
          <TextField
            margin="dense"
            label="Email (optional)"
            type="email"
            fullWidth
            variant="outlined"
            value={employeeEmail}
            onChange={(e) => {
              const value = e.target.value;
              setEmployeeEmail(value);
              scheduleAutoSave(employeeFirstName, employeeLastName, value, employeePhone, employeeExtId, employeeDesignation);
            }}
            disabled={dialogLoading}
          />
          <TextField
            margin="dense"
            label="Phone Number (optional)"
            fullWidth
            variant="outlined"
            value={employeePhone}
            onChange={(e) => {
              const value = e.target.value;
              setEmployeePhone(value);
              scheduleAutoSave(employeeFirstName, employeeLastName, employeeEmail, value, employeeExtId, employeeDesignation);
            }}
            disabled={dialogLoading}
            placeholder="(555) 555-0123"
          />
          <TextField
            margin="dense"
            label="Employee ID"
            fullWidth
            variant="outlined"
            value={employeeExtId}
            onChange={(e) => {
              const value = e.target.value;
              setEmployeeExtId(value);
              scheduleAutoSave(employeeFirstName, employeeLastName, employeeEmail, employeePhone, value, employeeDesignation);
            }}
            disabled={dialogLoading}
            placeholder="e.g., E12345 (defaults to 0)"
          />
          <TextField
            margin="dense"
            label="Role"
            fullWidth
            variant="outlined"
            value={employeeDesignation}
            onChange={(e) => {
              const value = e.target.value;
              setEmployeeDesignation(value);
              scheduleAutoSave(employeeFirstName, employeeLastName, employeeEmail, employeePhone, employeeExtId, value);
            }}
            disabled={dialogLoading}
            placeholder="e.g., Server, Bartender, Host"
          />
        </DialogContent>
        <DialogActions sx={editingEmployee ? { justifyContent: 'space-between' } : undefined}>
          {editingEmployee ? (
            <>
              <Typography
                variant="body2"
                color={autoSaveStatus === 'error' ? 'error' : 'text.secondary'}
                sx={{ display: 'flex', alignItems: 'center', gap: 1, pl: 1 }}
              >
                {autoSaveStatus === 'saving' && (
                  <>
                    <CircularProgress size={14} />
                    Saving…
                  </>
                )}
                {autoSaveStatus === 'pending' && 'Unsaved changes…'}
                {autoSaveStatus === 'saved' && 'All changes saved'}
                {autoSaveStatus === 'error' && (autoSaveError || 'Failed to save changes')}
              </Typography>
              <Button onClick={handleCloseDialog} variant="contained" disabled={autoSaveStatus === 'saving'}>
                Close
              </Button>
            </>
          ) : (
            <>
              <Button onClick={handleCloseDialog} disabled={dialogLoading}>
                Cancel
              </Button>
              <Button
                onClick={handleSaveEmployee}
                variant="contained"
                disabled={!employeeFirstName.trim() || !employeeLastName.trim() || dialogLoading}
              >
                {dialogLoading ? <CircularProgress size={20} /> : 'Save'}
              </Button>
            </>
          )}
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default EmployeesPage;
